/**
 * redactDebugPayload — recursive PII scrubber for the Debug Panel
 * (Bill 2026-04-24 mandate: clipboard/export must redact at write-time).
 *
 * Rules implemented:
 *   - Field-name match (case-insensitive) → mask string value
 *       /(?:^|[._-])(ssn|taxid|tin|ein|password|token|key|authorization|apiKey|fnKey|functionKey)(?:$|[._-])/i
 *     → kept as first 8 chars + "…" for tokens/keys, fully masked for SSN/TaxId.
 *   - Identification numbers / `idNumber` / `idNum` → ***-**-{last4} when length ≥ 4.
 *   - Phone numbers in any string value (regex /\b\d{3}[-.\s]?\d{3}[-.\s]?\d{4}\b/)
 *     → middle three digits replaced with ***.
 *   - Emails (/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i) → first char of local
 *     + ***@domain.
 *
 * The function is pure + idempotent. Safe to call on any input shape including
 * cyclic graphs (cycles are dropped to "[Circular]"). Strings are duplicated
 * before regex application so original objects aren't mutated.
 */

const SECRET_KEY_RE = /^(ssn|taxid|tin|ein|password|token|authorization|apikey|api_key|fnkey|functionkey|access_token|refresh_token|bearer|secret|x-functions-key)$/i;
const TOKENISH_KEY_RE = /(token|secret|password|authorization|apikey|api_key|functionkey|fn_key|x-functions-key)/i;
const IDENT_KEY_RE = /^(ssn|taxid|tin|ein|idnumber|idnum|identificationnumber|driverlicense|dlnumber)$/i;

const PHONE_RE = /\b(\d{3})[-.\s]?(\d{3})[-.\s]?(\d{4})\b/g;
const EMAIL_RE = /\b([A-Z0-9._%+-])[A-Z0-9._%+-]*@([A-Z0-9.-]+\.[A-Z]{2,})\b/gi;

function maskToken(value: string): string {
  if (!value) return value;
  if (value.length <= 8) return '***';
  return `${value.slice(0, 8)}…(${value.length}ch)`;
}

function maskFullSecret(): string {
  return '***REDACTED***';
}

function maskIdentLast4(value: string): string {
  const digits = value.replace(/\D+/g, '');
  if (digits.length < 4) return '***';
  return `***-**-${digits.slice(-4)}`;
}

function maskPhonesInString(s: string): string {
  return s.replace(PHONE_RE, (_m, _a: string, _b: string, c: string) => {
    return `***-***-${c}`;
  });
}

function maskEmailsInString(s: string): string {
  return s.replace(EMAIL_RE, (_m, first: string, domain: string) => {
    return `${first}***@${domain}`;
  });
}

function scrubString(value: string): string {
  let out = value;
  out = maskPhonesInString(out);
  out = maskEmailsInString(out);
  return out;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Public entry — redact a value (object / array / primitive). Always returns
 * a fresh structure; never mutates the input. Cycles map to '[Circular]'.
 */
export function redactDebugPayload(input: unknown): unknown {
  const seen = new WeakSet<object>();

  const walk = (val: unknown, parentKey?: string): unknown => {
    if (val === null || val === undefined) return val;

    if (typeof val === 'string') {
      // Parent-key driven masking
      if (parentKey) {
        if (SECRET_KEY_RE.test(parentKey)) {
          // Tokens / keys keep a short fingerprint for triage context;
          // SSNs/TaxIds get a full mask.
          if (/(ssn|taxid|tin|ein|password|secret)/i.test(parentKey)) {
            return maskFullSecret();
          }
          return maskToken(val);
        }
        if (IDENT_KEY_RE.test(parentKey)) {
          return maskIdentLast4(val);
        }
        if (TOKENISH_KEY_RE.test(parentKey)) {
          return maskToken(val);
        }
      }
      return scrubString(val);
    }

    if (typeof val === 'number' || typeof val === 'boolean' || typeof val === 'bigint') {
      return val;
    }

    if (val instanceof Date) return val.toISOString();
    if (val instanceof Error) {
      return {
        name: val.name,
        message: scrubString(val.message ?? ''),
        stack: val.stack ? scrubString(val.stack) : undefined,
      };
    }

    if (Array.isArray(val)) {
      if (seen.has(val)) return '[Circular]';
      seen.add(val);
      return val.map((v) => walk(v, parentKey));
    }

    if (isPlainObject(val)) {
      if (seen.has(val)) return '[Circular]';
      seen.add(val);
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(val)) {
        out[k] = walk(v, k);
      }
      return out;
    }

    // Functions / symbols / weird hosts → drop to descriptive marker.
    if (typeof val === 'function') return `[function ${val.name || 'anonymous'}]`;
    return String(val);
  };

  return walk(input);
}

/**
 * Test-helper export so a future Vitest suite can sanity-check rules without
 * spinning up the full panel. The primary `redactDebugPayload` is the only
 * symbol production code needs.
 */
export const __redactInternals = {
  maskToken,
  maskFullSecret,
  maskIdentLast4,
  maskPhonesInString,
  maskEmailsInString,
};
